import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { oneKlicks } from '../SDK/oneclick';
import { toast } from 'react-toastify';
import { Badge, Form, InputGroup, Button as BSButton } from 'react-bootstrap';
import { Button, Select } from 'antd';
import {
    SearchOutlined,
    PlusOutlined,
    EyeOutlined,
    CalendarOutlined,
    MessageOutlined,
    DeleteOutlined,
    GlobalOutlined,
    StarFilled,
    ReloadOutlined,
    HomeOutlined
} from '@ant-design/icons';
import { getWishlist, removeFromWishlist, encryptId, formatUrlString } from '../services/apiService';
import ImageShow from '../Uitiles/ImageShow';

const UserWishlist = () => {
    const { userData } = useAuth();
    const navigate = useNavigate();
    const [wishlist, setWishlist] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterSpecialization, setFilterSpecialization] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [selectedDoctors, setSelectedDoctors] = useState([]);

    useEffect(() => {
        fetchWishlist();
    }, []);

    const fetchWishlist = async () => {
        try {
            setLoading(true);
            const wishlistData = await getWishlist();

            if (wishlistData.length > 0) {
                // Extract doctor details from the joined 'doctor' field
                const doctorDetails = wishlistData.map(item => {
                    const doctor = item.doctor?.[0] || {};

                    // Attempt to extract specialization from nested join data
                    let specialization = '';

                    // Try to get from joined doctor_specialities
                    if (doctor.doctor_specialities && doctor.doctor_specialities.length > 0) {
                        const firstSpecObj = doctor.doctor_specialities[0];
                        if (firstSpecObj.specialities && firstSpecObj.specialities.length > 0) {
                            specialization = firstSpecObj.specialities[0].name;
                        }
                    }

                    // Fallback to existing fields
                    if (!specialization) specialization = doctor.specialization;
                    if (!specialization && doctor.speciality_ids) {
                        try {
                            const parsed = JSON.parse(doctor.speciality_ids);
                            const firstSpec = Object.values(parsed)[0];
                            if (firstSpec && (firstSpec.name || firstSpec.speciality_name)) {
                                specialization = firstSpec.name || firstSpec.speciality_name;
                            }
                        } catch (e) { }
                    }

                    return {
                        ...item,
                        doctor_name: doctor.display_name || doctor.name || 'Unknown Doctor',
                        doctor_specialization: specialization || doctor.category || 'Consultant',
                        doctor_experience: doctor.experience || 'Not specified',
                        doctor_consultation_fee: doctor.consultation_fee || doctor.starting_price || 'Not specified',
                        doctor_profile_photo_url: doctor.profile_photo_url || doctor.profile_url || null,
                        doctor_phone: doctor.phone || 'Not specified',
                        doctor_email: doctor.email || 'Not specified',
                        doctor_rating: doctor.rating || 4.5,
                        doctor_reviews: doctor.reviews_count || 0,
                        doctor_location: doctor.location || doctor.address || 'Not specified',
                        doctor_availability: doctor.availability || 'Available',
                        doctor_languages: doctor.languages || 'English, Hindi',
                        doctor_education: doctor.education || doctor.qualification || 'MBBS, MD',
                        doctor_hospital: doctor.hospital_name || 'Not specified'
                    };
                });
                setWishlist(doctorDetails);
            } else {
                setWishlist([]);
            }
        } catch (error) {
            console.error('Error fetching wishlist:', error);
            toast.error('Failed to load wishlist');
            setWishlist([]);
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveFromWishlist = async (doctorId, doctorName) => {
        if (window.confirm(`Are you sure you want to remove Dr. ${doctorName} from your wishlist?`)) {
            try {
                await removeFromWishlist(doctorId);
                toast.success('Doctor removed from wishlist');
                fetchWishlist();
            } catch (error) {
                console.error('Error removing from wishlist:', error);
                toast.error('Failed to remove from wishlist');
            }
        }
    };

    const handleViewDoctorProfile = (doctorName, doctorId) => {
        const doctorUrl = `/doctor/${formatUrlString(doctorName)}/${encryptId(doctorId)}`;
        navigate(doctorUrl);
    };

    const handleBookAppointment = (doctorName, doctorId) => {
        const doctorUrl = `/doctor/${formatUrlString(doctorName)}/${encryptId(doctorId)}?book=true`;
        navigate(doctorUrl);
    };

    const handleChatWithDoctor = (doctorId, doctorName) => {
        navigate(`/user-chat?doctor=${doctorId}&name=${encodeURIComponent(doctorName)}`);
    };

    // Filter and sort wishlist
    const filteredWishlist = wishlist
        .filter(doctor =>
            doctor.doctor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            doctor.doctor_specialization?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            doctor.doctor_location?.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .filter(doctor =>
            !filterSpecialization || doctor.doctor_specialization === filterSpecialization
        )
        .sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return (a.doctor_name || '').localeCompare(b.doctor_name || '');
                case 'specialization':
                    return (a.doctor_specialization || '').localeCompare(b.doctor_specialization || '');
                case 'fee':
                    return (parseFloat(a.doctor_consultation_fee) || 0) - (parseFloat(b.doctor_consultation_fee) || 0);
                case 'rating':
                    return (parseFloat(b.doctor_rating) || 0) - (parseFloat(a.doctor_rating) || 0);
                case 'experience':
                    return (parseInt(a.doctor_experience) || 0) - (parseInt(b.doctor_experience) || 0);
                default:
                    return 0;
            }
        });

    // Get unique specializations for filter
    const specializations = [...new Set(wishlist.map(doctor => doctor.doctor_specialization).filter(Boolean))];

    const renderStars = (rating) => {
        return (
            <div className="d-flex align-items-center gap-1">
                <StarFilled style={{ color: '#fbbf24', fontSize: '11px' }} />
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#111827' }}>{rating || '4.5'}</span>
            </div>
        );
    };

    return (
        <section className="dc-haslayout dc-dbsectionspace">
            <style>
                {`
                    @media (max-width: 767px) {
                        .dc-table-responsive thead {
                            display: none;
                        }
                        .dc-table-responsive tbody tr {
                            display: block;
                            margin-bottom: 15px;
                            border: 1px solid #eee;
                            border-radius: 8px;
                            background: #fff;
                            box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                        }
                        .dc-table-responsive tbody td {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            padding: 12px 15px !important;
                            border-bottom: 1px solid #f5f5f5;
                            text-align: right;
                            font-size: 13px;
                            min-height: 45px;
                        }
                        .dc-table-responsive tbody td:last-child {
                            border-bottom: 0;
                        }
                        .dc-table-responsive tbody td::before {
                            content: attr(data-label);
                            font-weight: 700;
                            color: #666;
                            text-transform: uppercase;
                            font-size: 10px;
                            letter-spacing: 0.5px;
                            text-align: left;
                            flex: 1;
                        }
                    }
                    .specialization-tag {
                        font-size: 11px;
                        padding: 2px 8px;
                        border-radius: 4px;
                        background: #f0fdf4;
                        color: #166534;
                        border: 1px solid #dcfce7;
                        font-weight: 600;
                        display: inline-block;
                    }
                `}
            </style>
            <div className="subbody" style={{ width: '100%', padding: '2% 4%' }}>
                <div className="container-fluid py-3">
                    {/* Header Card matching UserMedicalRecords */}
                    <div className="card mb-4" style={{ borderRadius: '5px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                        <div className="card-body" style={{ padding: '24px 30px' }}>
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
                                <div className="d-flex align-items-center gap-2">
                                    <h5 className="mb-0 font-weight-bold">My Wishlist</h5>
                                    <Badge bg="info" style={{ borderRadius: '5px', padding: '5px 10px', fontSize: '12px' }}>Favorites</Badge>
                                </div>
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <BSButton
                                            variant="outline-primary"
                                            onClick={fetchWishlist}
                                            style={{ height: '40px', borderRadius: "5px", display: 'flex', alignItems: 'center', gap: '8px' }}
                                        >
                                            <ReloadOutlined /> Refresh
                                        </BSButton>
                                        <BSButton
                                            className="btn-primary"
                                            style={{
                                                height: '40px',
                                                borderRadius: "5px",
                                                backgroundColor: 'var(--themecolor)',
                                                borderColor: 'var(--themecolor)',
                                                color: '#fff',
                                                fontWeight: '600',
                                                transition: 'all 0.3s ease',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                padding: '0 20px'
                                            }}
                                            onClick={() => navigate('/search-doctors')}
                                        >
                                            <PlusOutlined /> Explore More
                                        </BSButton>
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex flex-wrap align-items-center gap-3">
                                <div style={{ flex: '1', minWidth: '250px', maxWidth: '400px' }}>
                                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                                        Search Doctors
                                    </label>
                                    <InputGroup>
                                        <InputGroup.Text style={{ backgroundColor: '#fff', borderRight: 'none', borderRadius: '5px 0 0 5px' }}>
                                            <SearchOutlined style={{ color: '#666' }} />
                                        </InputGroup.Text>
                                        <Form.Control
                                            placeholder="Search by name or clinic..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            style={{ height: '38px', borderLeft: 'none', borderRadius: "0 5px 5px 0", fontSize: '14px' }}
                                        />
                                    </InputGroup>
                                </div>
                                <div style={{ minWidth: '160px' }}>
                                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                                        Specialization
                                    </label>
                                    <select
                                        value={filterSpecialization}
                                        onChange={(e) => setFilterSpecialization(e.target.value)}
                                        className="form-control"
                                        style={{ width: '100%', height: '38px', borderRadius: '5px' }}
                                    >
                                        <option value="">All Specialities</option>
                                        {specializations.map(spec => (
                                            <option key={spec} value={spec}>{spec}</option>
                                        ))}
                                    </select>
                                </div>
                                <div style={{ minWidth: '160px' }}>
                                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                                        Sort By
                                    </label>
                                    <select
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                        className="form-control"
                                        style={{ width: '100%', height: '38px', borderRadius: '5px' }}
                                    >
                                        <option value="name">Doctor Name</option>
                                        <option value="fee">Price (L-H)</option>
                                        <option value="rating">Top Rated</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table Card matching UserMedicalRecords */}
                    <div className="card" style={{ borderRadius: '5px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                        <div className="card-body p-0">
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-2 text-muted">Fetching your wishlist...</p>
                                </div>
                            ) : filteredWishlist.length === 0 ? (
                                <div className="text-center py-5">
                                    <div className="dc-emptydata-holder">
                                        <div className="dc-emptydata">
                                            <div className="dc-emptydetails dc-empty-booking dc-emptyholder-sm">
                                                <span></span>
                                                <em className="text-muted">Your wishlist is empty</em>
                                            </div>
                                        </div>
                                    </div>
                                    <Button type="primary" onClick={() => navigate('/search-doctors')} style={{ borderRadius: '5px', marginTop: '15px' }}>
                                        Explore Doctors
                                    </Button>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0 align-middle dc-table-responsive">
                                        <thead className="bg-light">
                                            <tr style={{ whiteSpace: 'nowrap' }}>
                                                <th data-label="#" style={{ padding: '10px 15px', fontSize: '13px', fontWeight: '600', color: '#666', textAlign: 'left' }}>#</th>
                                                <th data-label="Doctor Info" style={{ padding: '10px 15px', fontSize: '13px', fontWeight: '600', color: '#666', textAlign: 'left' }}>Doctor Info</th>
                                                <th data-label="Specialization" style={{ padding: '10px 15px', fontSize: '13px', fontWeight: '600', color: '#666', textAlign: 'left' }}>Specialization</th>
                                                <th data-label="Fee / Rating" style={{ padding: '10px 15px', fontSize: '13px', fontWeight: '600', color: '#666', textAlign: 'left' }}>Fee / Rating</th>
                                                <th data-label="Actions" style={{ padding: '10px 15px', fontSize: '13px', fontWeight: '600', color: '#666', textAlign: 'right' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="border-top-0">
                                            {filteredWishlist.map((doctor, index) => (
                                                <tr key={doctor.id || index}>
                                                    <td data-label="#" style={{ padding: '8px 15px', verticalAlign: 'middle', fontSize: '13px' }}>{index + 1}</td>
                                                    <td data-label="Doctor Info" style={{ padding: '12px 15px', verticalAlign: 'middle' }}>
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div style={{ width: '45px', height: '45px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #f1f5f9', flexShrink: 0 }}>
                                                                <ImageShow
                                                                    url={doctor.doctor_profile_photo_url || "/uploads/3/doctors/default.png"}
                                                                    width={45} height={45} style={{ objectFit: 'cover' }}
                                                                />
                                                            </div>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                                <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '14px', lineHeight: '1.2' }}>{doctor.doctor_name}</div>
                                                                <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                                    <HomeOutlined style={{ fontSize: '11px', color: '#94a3b8' }} />
                                                                    <span style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                        {doctor.doctor_hospital}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td data-label="Specialization" style={{ padding: '12px 15px', verticalAlign: 'middle', textAlign: 'left' }}>
                                                        <span className="specialization-tag">{doctor.doctor_specialization}</span>
                                                    </td>
                                                    <td data-label="Fee" style={{ padding: '12px 15px', verticalAlign: 'middle', textAlign: 'left' }}>
                                                        <div className="d-flex flex-column gap-1" style={{ alignItems: 'flex-start' }}>
                                                            <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '14px' }}>₹{doctor.doctor_consultation_fee}</div>
                                                            {renderStars(doctor.doctor_rating)}
                                                        </div>
                                                    </td>
                                                    <td data-label="Actions" style={{ padding: '8px 15px', verticalAlign: 'middle' }}>
                                                        <div className="d-flex gap-2 justify-content-end align-items-center">
                                                            <Button
                                                                size="small"
                                                                onClick={() => handleViewDoctorProfile(doctor.doctor_name, doctor.doctor_id)}
                                                                style={{ borderRadius: '4px', width: '32px', height: '32px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                                icon={<EyeOutlined style={{ fontSize: '14px' }} />}
                                                                title="View Profile"
                                                            />
                                                            <Button
                                                                size="small"
                                                                className="btn-success"
                                                                onClick={() => handleBookAppointment(doctor.doctor_name, doctor.doctor_id)}
                                                                style={{ borderRadius: '4px', width: '32px', height: '32px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#28a745', borderColor: '#28a745', color: '#fff' }}
                                                                icon={<CalendarOutlined style={{ fontSize: '14px' }} />}
                                                                title="Book Appointment"
                                                            />
                                                            <Button
                                                                size="small"
                                                                onClick={() => handleChatWithDoctor(doctor.doctor_id, doctor.doctor_name)}
                                                                style={{ borderRadius: '4px', width: '32px', height: '32px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                                icon={<MessageOutlined style={{ fontSize: '14px' }} />}
                                                                title="Chat"
                                                            />
                                                            <Button
                                                                size="small"
                                                                danger
                                                                onClick={() => handleRemoveFromWishlist(doctor.doctor_id, doctor.doctor_name)}
                                                                style={{ borderRadius: '4px', width: '32px', height: '32px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                                icon={<DeleteOutlined style={{ fontSize: '14px' }} />}
                                                                title="Remove"
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default UserWishlist;
